import React from 'react';
import PropTypes from 'prop-types';
import './y-page-shell.css';

export function YPageShell({ testId, className = '', children }) {
  return (
    <div
      className={['y-page-shell', className].filter(Boolean).join(' ')}
      data-testid={testId}
      data-yona="page-shell"
    >
      {children}
    </div>
  );
}

YPageShell.propTypes = {
  testId: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export function YPageShellHeader({
  icon,
  title,
  subtitle,
  actions = null,
  tabs = null,
  titleVariant = 'mono',
  iconVariant = 'default',
}) {
  const titleClass = [
    'y-page-shell__title',
    titleVariant === 'serif' ? 'y-page-shell__title--serif' : 'y-page-shell__title--mono',
  ].join(' ');

  const iconClass = [
    'y-page-shell__icon',
    iconVariant === 'avatar' ? 'y-page-shell__icon--avatar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header className="y-page-shell__header">
      <div className="y-page-shell__header-top">
        <div className="y-page-shell__brand">
          <div className={iconClass} aria-hidden={iconVariant !== 'avatar'}>
            {icon}
          </div>
          <div className="y-page-shell__brand-text">
            <h1 className={titleClass}>{title}</h1>
            {subtitle ? <p className="y-page-shell__subtitle">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? <div className="y-page-shell__actions">{actions}</div> : null}
      </div>
      {tabs}
    </header>
  );
}

YPageShellHeader.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  tabs: PropTypes.node,
  titleVariant: PropTypes.oneOf(['mono', 'serif']),
  iconVariant: PropTypes.oneOf(['default', 'avatar']),
};

export function YPageShellTabs({
  tabs,
  activeId,
  onTabChange,
  ariaLabel,
  nested = false,
  className = '',
}) {
  return (
    <div
      className={[
        'y-page-shell__tabs',
        nested ? 'y-page-shell__tabs--nested' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeId === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`y-page-shell__tab${isActive ? ' is-active' : ''}`}
            onClick={() => onTabChange(tab.id, tab)}
          >
            {Icon ? <Icon aria-hidden="true" /> : null}
            <span>{tab.label}</span>
            {tab.badge > 0 ? (
              <span className="y-page-shell__tab-badge">{tab.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

YPageShellTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      badge: PropTypes.number,
      path: PropTypes.string,
    })
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  nested: PropTypes.bool,
  className: PropTypes.string,
};

export function YPageShellSegmentedTabs({
  tabs,
  activeId,
  onTabChange,
  ariaLabel,
  className = '',
}) {
  return (
    <div
      className={['y-page-shell__segmented', className].filter(Boolean).join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeId === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`y-page-shell__segmented-tab${isActive ? ' is-active' : ''}`}
            onClick={() => onTabChange(tab.id, tab)}
          >
            {Icon ? <Icon aria-hidden="true" /> : null}
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

YPageShellSegmentedTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
    })
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export function YPageShellBody({ children, className = '' }) {
  return (
    <div className={['y-page-shell__body', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

YPageShellBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};
